import { useEffect, useState } from "react";

import type {
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissional,
} from "@/modules/carga-trabalho/carga-trabalho.types";
import { carregarAlocacoesDoPeriodo } from "./nova-obra.verificacoes";

export type AlocacoesPeriodoStatus = "idle" | "loading" | "success" | "error";

export interface AlocacoesPeriodoState {
  status: AlocacoesPeriodoStatus;
  profissionais: CargaTrabalhoProfissional[];
}

const IDLE: AlocacoesPeriodoState = { status: "idle", profissionais: [] };

/**
 * Alocações dos profissionais da equipe no período da obra (carga de trabalho, HU 1779) para a Revisão do wizard avisar
 * conflitos (RN15). Carrega quando há profissionais e a cada mudança de equipe ou período; falha na consulta vira
 * `error` e quem usa só omite o aviso. Sem `profissionalIds` consulta sem lista: o core devolve quem tem alocação no
 * período (a Etapa 4 mostra os conflitos também nos candidatos do menu, não só na equipe); com a lista vazia não consulta.
 */
export function useAlocacoesPeriodo({
  periodo,
  profissionalIds,
}: {
  periodo: CargaTrabalhoPeriodo;
  profissionalIds?: string[];
}): AlocacoesPeriodoState {
  const [state, setState] = useState<AlocacoesPeriodoState>(IDLE);
  const { inicio, fim } = periodo;
  // Chave estável: a lista chega recriada a cada render.
  const todos = profissionalIds === undefined;
  const idsKey = [...new Set(profissionalIds)].sort().join("|");

  useEffect(() => {
    if (!idsKey && !todos) return;
    let cancelado = false;
    const controller = new AbortController();
    setState((anterior) => ({ ...anterior, status: "loading" }));

    carregarAlocacoesDoPeriodo({ inicio, fim }, idsKey ? idsKey.split("|") : [], controller.signal)
      .then((profissionais) => {
        if (!cancelado) setState({ status: "success", profissionais });
      })
      .catch(() => {
        if (!cancelado) setState({ status: "error", profissionais: [] });
      });

    return () => {
      cancelado = true;
      controller.abort();
    };
  }, [idsKey, todos, inicio, fim]);

  return state;
}
